import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { ComponentStatus, StateComponent } from '../../../../../common/components/state/state.component';
import { ActivatedRoute } from '@angular/router';
import { ICompetitionGroupInfoModel, ICompetitionGroupParticipantDtoModel } from '../../../../../common/apis/tournament/model/tournament.model';
import { MatDialog } from '@angular/material/dialog';
import { ParticipantsLinkDialogComponent, ParticipantsLinkDialogComponentData } from './components/participants-link-dialog/participants-link-dialog.component';
import { CompetitionGroupCommonService } from '../common/services/competition-group-common.service';
import { ConfirmDialogComponent, ConfirmDialogComponentData } from '../../../../../common/components/confirm-dialog/confirm-dialog.component';
import { NotificationService } from '../../../../../common/services/notification.service';
import { AppStateService } from '../../../../../common/services/app-state.service';
import { AnalyticsService } from '../../../../../common/services/analytics.service';
import { ParticipantRole } from '@oogaday/shared';
import { CompetitionGroupApiService } from '../../../../../common/apis/competition-group/competition-group-api.service';
import { ICompetitionGroupModel } from '../../../../../common/apis/competition-group/model/competitoin-group.model';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatIconButton } from '@angular/material/button';
import { MatChipListbox, MatChipOption } from '@angular/material/chips';
import { MatList, MatListItem, MatListItemAvatar, MatListItemTitle } from '@angular/material/list';
import { MatCard, MatCardContent } from '@angular/material/card';
import { ListHeaderComponent } from '../../../../../common/components/list-header/list-header.component';
import { OverlaySpinnerComponent } from '../../../../../common/components/overlay-spinner/overlay-spinner.component';
import { NgOptimizedImage } from '@angular/common';
import { LoadingContainerComponent } from '../../../../../common/components/loading-container/loading-container.component';

@Component({
  selector: 'oo-participants-list',
  templateUrl: './participants-list.component.html',
  styleUrls: ['./participants-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LoadingContainerComponent,
    OverlaySpinnerComponent,
    ListHeaderComponent,
    MatCard,
    MatCardContent,
    MatList,
    MatListItem,
    MatListItemAvatar,
    MatListItemTitle,
    MatChipListbox,
    MatChipOption,
    MatIconButton,
    MatMenuTrigger,
    MatIcon,
    MatMenu,
    MatMenuItem,
    NgOptimizedImage,
  ],
})
export class ParticipantsListComponent extends StateComponent<ParticipantsListComponentState> implements OnInit {

  protected readonly participantRole = ParticipantRole;
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly compGroupApi = inject(CompetitionGroupApiService);
  private readonly tournamentCommonService = inject(CompetitionGroupCommonService);
  private readonly notificationService = inject(NotificationService);
  private readonly appStateService = inject(AppStateService);
  state: ParticipantsListComponentState = {
    status: 'ready',
    compGroup: this.tournamentCommonService.getCompGroupInfo(this.route.snapshot.paramMap.get('compGroupId')!),
    participants: [],
    currentUserId: this.appStateService.getAppData().user.id,
    currentUserRole: ParticipantRole.MEMBER,
    tourInfo: null!,
  };
  private readonly analyticsService = inject(AnalyticsService);

  ngOnInit(): void {
    this.analyticsService.trackScreenName('participantsList');
    this.fetchData();
  }

  openInviteDialog(): void {
    this.dialog.open(ParticipantsLinkDialogComponent, {
      data: {
        compGroupId: this.state.compGroup.id,
      } as ParticipantsLinkDialogComponentData,
    });
  }

  async deleteParticipant(participant: ICompetitionGroupParticipantDtoModel): Promise<void> {
    const agree = await this.dialog.open(ConfirmDialogComponent, {
      data: {
        header: 'Are you sure?',
        content: `By confirming <b>${participant.firstName} ${participant.lastName}</b> will be removed from the participants list.`,
      } as ConfirmDialogComponentData,
    }).afterClosed()
      .toPromise();

    if (agree) {
      this.updateStatus('saving');
      this.compGroupApi.removeParticipant(this.state.compGroup.id, participant.id)
        .then(() => {
          this.notificationService.notify('User was removed');

          return this.fetchData();
        })
        .catch((e: Error) => {
          console.error(e);
          this.updateStatus('ready');
          this.notificationService.notifyError();
        });
    }
  }

  switchRole(participantId: string, role: ParticipantRole): void {
    this.updateStatus('loading');
    this.compGroupApi.updateParticipantRole(this.state.compGroup.id, participantId, role)
      .then(() => {
        this.notificationService.notify('Role was changed');
        const participant = this.state.participants.find(({ id }) => id === participantId);
        participant!.role = role;
      })
      .catch((error) => {
        console.error(error);
        this.notificationService.notifyError();
      })
      .finally(() => {
        this.updateStatus('ready');
      });
  }

  private fetchData(): void {
    this.updateStatus('loading');
    Promise.all([
      this.compGroupApi.getCompGroupParticipants(this.state.compGroup.id),
      this.compGroupApi.getCompGroupInfo(this.state.compGroup.id, ['inviteLink', 'tournamentStarted']),
    ])
      .then(([participants, tourInfo]) => {
        this.updateState(
          {
            status: 'ready',
            participants,
            tourInfo,
            currentUserRole: participants.find(({ userId }) => this.state.currentUserId === userId)!.role,
          },
        );
      })
      .catch((e: Error) => {
        console.error(e);
        this.updateStatus('error');
      });
  }
}

interface ParticipantsListComponentState {
  status: ComponentStatus;
  participants: ICompetitionGroupParticipantDtoModel[];
  compGroup: ICompetitionGroupModel;
  currentUserId: string;
  currentUserRole: ParticipantRole;
  tourInfo: ICompetitionGroupInfoModel<'inviteLink' | 'tournamentStarted'>;
}
